import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import { APIResponse } from "./CommonApiSlice";
import { PaginationType } from "@src/types/CommonTypes";

interface CreateSyllabusPayload {
  name: string;
}

type AddSylllabusResponse = APIResponse;
type UpdateSylllabusResponse = APIResponse;

export interface Subject {
  createdOn: string;
  lastUpdateOn?: string;
  createdByName: string;
  createdByEmail: string;
  modifiedByName?: string;
  modifiedByEmail?: string;
  id: string;
  name: string;
  category: string;
  questionType: Array<string>;
  isActive: boolean;
  description?: string;
  overview?: string;
  type: string;
  programmingLanguage: string[];
  searchKey: string;
  logo?: string;
  icon?: string;
  subjectLogo?: string;
  isLogoPresent?: boolean;
  defaultScript?: string;
}

export interface SubjectSyllabus {
  syllabusId: string;
  subject: Subject;
}

export interface GetAllSyllabusForTrainerResponse extends APIResponse {
  response: { content: SubjectSyllabus[] } & PaginationType;
}

export const SyllabusApiSlice = createApi({
  reducerPath: "syllabusApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    addSyllabus: builder.mutation<
      AddSylllabusResponse,
      { payload: CreateSyllabusPayload }
    >({
      query: ({ payload }) => ({
        url: `syllabus`,
        method: "POST",
        body: payload,
      }),
    }),
    updateSyllabus: builder.mutation<
      UpdateSylllabusResponse,
      { payload: CreateSyllabusPayload; syllabusId: string }
    >({
      query: ({ payload, syllabusId }) => ({
        url: `syllabus`,
        method: "PUT",
        body: payload,
        params: { syllabusId },
      }),
    }),
    getAllSyllabusForTrainer: builder.query<
      GetAllSyllabusForTrainerResponse,
      { pageNo: number; pageSize: number; search?: string }
    >({
      query: ({ pageNo, pageSize, search }) => {
        let url = `syllabus/get-syllabus-trainer?pageNo=${pageNo}&pageSize=${pageSize}`;
        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }
        return {
          url,
          method: "GET",
        };
      },
    }),
    getSyllabusForOrganization: builder.query<
      any,
      { organizationId: string; organizationType: string; subjectId: string }
    >({
      query: ({ organizationId, organizationType, subjectId }) => ({
        url: `syllabus/get-syllabus-organization`,
        method: "GET",
        params: { organizationId, organizationType, subjectId },
      }),
    }),
    getOrganizationListforSyllabus: builder.query<
      any,
      {
        subjectId: string;
        organizationId: string;
        pageNo: number;
        pageSize: number;
      }
    >({
      query: ({ subjectId, organizationId, pageNo, pageSize }) => ({
        url: `syllabus/get-organization-subject`,
        method: "GET",
        params: { subjectId, organizationId, pageNo, pageSize },
      }),
    }),
  }),
});

export const {
  useAddSyllabusMutation,
  useGetAllSyllabusForTrainerQuery,
  useGetSyllabusForOrganizationQuery,
  useLazyGetSyllabusForOrganizationQuery,
  useGetOrganizationListforSyllabusQuery,
  useLazyGetOrganizationListforSyllabusQuery,
  useUpdateSyllabusMutation,
  useLazyGetAllSyllabusForTrainerQuery,
} = SyllabusApiSlice;
